import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { findCityBySlug, photographerMatchesCity, type CityInfo } from "@/data/cities";
import { visagistThemes, type Visagist, type VisagistTheme } from "@/data/visagists";
import { fetchAllVisagists } from "@/lib/visagists-source";
import { PortraitImage } from "@/components/PortraitImage";

type ThemeData = (typeof visagistThemes)[number];

export const Route = createFileRoute("/visagist/$city/$theme")({
  loader: ({ params }) => {
    const city = findCityBySlug(params.city);
    const theme = visagistThemes.find((t) => t.id === params.theme);
    if (!city || !theme) throw notFound();
    return { city, theme };
  },
  head: ({ loaderData, params }) => {
    const city = loaderData?.city;
    const theme = loaderData?.theme;
    if (!city || !theme) return { meta: [{ title: "Visagist:in, OM Selected" }] };
    const title = `${theme.label} Visagist:in ${city.name}, OM Selected`;
    const description = `Kuratierte Visagist:innen für ${theme.label} in ${city.name} (${city.country}). ${theme.description} Redaktionell empfohlen von OM Selected.`;
    const url = `https://omselected.art/visagist/${params.city}/${params.theme}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: title,
            description,
            url,
            about: {
              "@type": "Place",
              name: city.name,
              address: { "@type": "PostalAddress", addressLocality: city.name, addressCountry: city.country },
            },
            isPartOf: { "@type": "WebSite", name: "OM Selected", url: "https://omselected.art" },
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-[800px] px-5 py-32 text-center">
      <h1 className="serif text-4xl">Seite nicht gefunden</h1>
      <Link to="/visagists" className="eyebrow inline-block mt-8 border-b border-foreground pb-1">
        Alle Visagist:innen ansehen
      </Link>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-[800px] px-5 py-32 text-center">
      <h1 className="serif text-4xl">Fehler</h1>
      <p className="mt-4 text-muted-foreground">{error.message}</p>
    </div>
  ),
  component: CityThemePage,
});

function CityThemePage() {
  const { t } = useTranslation();
  const { city, theme } = Route.useLoaderData() as { city: CityInfo; theme: ThemeData };
  const [all, setAll] = useState<Visagist[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    fetchAllVisagists().then((data) => {
      if (active) {
        setAll(data);
        setLoading(false);
      }
    });
    return () => { active = false; };
  }, []);

  const matching = all.filter(
    (p) =>
      (photographerMatchesCity(p.city, city.name) || photographerMatchesCity(p.region, city.name)) &&
      p.themes.includes(theme.id as VisagistTheme),
  );
  const otherThemes = visagistThemes.filter((t) => t.id !== theme.id);

  return (
    <div>
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 pt-12 pb-16">
          <div className="eyebrow text-muted-foreground">
            <Link to="/visagist/$city" params={{ city: city.slug }} className="hover:text-foreground">
              Visagist:in {city.name}
            </Link>
            {" · "}
            {theme.label}
          </div>
          <h1 className="serif text-5xl md:text-7xl mt-6 leading-[0.95]">
            {theme.label} in {city.name}
          </h1>
          <p className="serif italic text-xl md:text-2xl mt-8 max-w-2xl">
            {theme.description} Kuratierte Auswahl für {city.name} und Umgebung.
          </p>
          <div className="mt-8 flex flex-wrap gap-4 eyebrow text-muted-foreground">
            {otherThemes.map((t) => (
              <Link
                key={t.id}
                to="/visagist/$city/$theme"
                params={{ city: city.slug, theme: t.id }}
                className="hover:text-foreground"
              >
                {t.label} in {city.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-16">
          <h2 className="eyebrow text-muted-foreground mb-8 font-normal">
            {theme.label} · {city.name}
          </h2>
          {loading ? (
            <div className="text-xs text-muted-foreground italic">{t("common.loading")}</div>
          ) : matching.length === 0 ? (
            <div className="space-y-6 max-w-xl">
              <p className="text-muted-foreground">
                Noch keine gelisteten Visagist:innen für {theme.label} in {city.name}.
              </p>
              <div className="flex flex-wrap gap-4 eyebrow">
                <Link to="/visagist/$city" params={{ city: city.slug }} className="border-b border-foreground pb-1">
                  Alle Visagist:innen in {city.name}
                </Link>
                <Link to="/brief" className="border-b border-foreground pb-1">Anfrage senden</Link>
              </div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
              {matching.map((p) => (
                <Link key={p.id} to="/visagists/$id" params={{ id: p.id }} className="block group cursor-pointer">
                  <PortraitImage p={p} />
                  <div className="mt-4 flex justify-between items-baseline">
                    <h3 className="serif text-2xl">{p.name}</h3>
                    <div className="eyebrow text-muted-foreground">{p.city ?? p.region}</div>
                  </div>
                  <div className="mt-2 eyebrow text-muted-foreground">
                    {p.themes.map((t) => visagistThemes.find((th) => th.id === t)?.label).join(" · ")}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}